import ApperIcon from '@/components/ApperIcon';
import { DetailRow } from '@/components/kit';
import { planName } from '@/shared/pricing';
import { cn } from '@/lib/utils';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

const TONES = {
  good: 'border-success-border bg-success-muted text-success',
  warn: 'border-warning-border bg-warning-muted text-warning',
  bad: 'border-destructive/40 bg-destructive/10 text-destructive',
  quiet: 'border-border bg-muted text-muted-foreground',
};

/** The Verified pill shown on orders and the account overview. */
export function VerifiedBadge({ at, className }) {
  if (!at) return null;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold',
        TONES.good,
        className,
      )}
      title={`Verified ${formatLocalDate(at, DATE_FORMATS.SHORT)}`}
    >
      <ApperIcon name="ShieldCheck" size={14} />
      Verified
    </span>
  );
}

/** Headline for a verify result, worded for the buyer. */
function headline(result) {
  if (!result.signIn?.ok) {
    return result.signIn?.reason === 'unreachable'
      ? {
          tone: 'warn',
          icon: 'WifiOff',
          title: 'We could not reach your line',
          text: 'The server did not answer our checker. Your line may still work fine in your app. Try again in a few minutes.',
        }
      : {
          tone: 'bad',
          icon: 'KeyRound',
          title: 'Your line did not sign in',
          text: 'The server turned down the username and password saved on this order. Open a ticket and we will sort it out.',
        };
  }
  if (result.genuine === true) {
    return {
      tone: 'good',
      icon: 'ShieldCheck',
      title: 'Genuine Ghost Cable',
      text: result.verified
        ? 'This line is ours and it is active.'
        : 'This line is ours, but it is not active right now, so it is not marked Verified.',
    };
  }
  if (result.genuine === false) {
    return {
      tone: 'bad',
      icon: 'ShieldX',
      title: 'Not recognised',
      text: 'This line does not match a Ghost Cable line. If you bought it from us, open a ticket so we can look.',
    };
  }
  return {
    tone: 'quiet',
    icon: 'ShieldQuestion',
    title: 'Genuine check not ready yet',
    text: 'Your line signed in, but we have not finished setting up the genuine check. Try again later.',
  };
}

function connections(line) {
  if (line.maxConnections === null && line.activeConnections === null) return null;
  const active = line.activeConnections ?? 0;
  const max = line.maxConnections ?? '?';
  return `${active} in use of ${max}`;
}

/** Result card for an IPTV Verifier run. `detail` (team only) adds the match signals. */
export default function VerifierResult({ result }) {
  if (!result) return null;
  const h = headline(result);
  const line = result.line;
  const detail = result.detail;

  return (
    <div className="space-y-4">
      <div className={cn('flex items-start gap-3 rounded-lg border px-4 py-4', TONES[h.tone])} role="status">
        <ApperIcon name={h.icon} size={28} className="mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-heading text-2xl leading-tight">{h.title}</p>
            {result.verified && <VerifiedBadge at={result.verifiedAt || new Date().toISOString()} />}
          </div>
          <p className="mt-1 text-sm text-foreground">{h.text}</p>
          {result.order?.Name && (
            <p className="mt-1 text-xs text-muted-foreground">
              Order <span className="font-mono">{result.order.Name}</span>
              {result.order.plan ? ` · ${planName(result.order.plan)}` : ''}
            </p>
          )}
        </div>
      </div>

      {line && (
        <dl className="divide-y divide-border rounded-lg border border-border px-4">
          <DetailRow label="Sign-in">
            <span className="inline-flex items-center gap-1.5">
              <ApperIcon
                name={line.status.toLowerCase() === 'active' ? 'CircleCheck' : 'CircleAlert'}
                size={16}
                className={line.status.toLowerCase() === 'active' ? 'text-success' : 'text-warning'}
              />
              {line.status}
            </span>
          </DetailRow>
          <DetailRow label="Expires">
            {line.noExpiry ? 'No end date on the line' : formatLocalDate(line.expiresAt, DATE_FORMATS.LONG)}
          </DetailRow>
          <DetailRow label="Connections">{connections(line)}</DetailRow>
          <DetailRow label="Type">{line.isTrial ? 'Trial' : 'Paid'}</DetailRow>
        </dl>
      )}

      {result.verifiedAt && !result.verified && result.signIn?.reason === 'unreachable' && (
        <p className="text-xs text-muted-foreground">
          Your Verified badge from {formatLocalDate(result.verifiedAt, DATE_FORMATS.SHORT)} stays until we can check again.
        </p>
      )}

      {detail && <TeamDetail detail={detail} />}
    </div>
  );
}

function TeamDetail({ detail }) {
  const scan = detail.scan;
  return (
    <div className="rounded-lg border border-dashed border-border p-4">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-muted-foreground">
        <ApperIcon name="Lock" size={14} />
        Team only · match signals
      </p>
      {!detail.references ? (
        <p className="mt-2 text-sm text-muted-foreground">
          No reference lines are saved yet, so nothing was compared. Add one on the IPTV Verifier admin page.
        </p>
      ) : !detail.matches?.length ? (
        <p className="mt-2 text-sm text-muted-foreground">The line did not sign in, so nothing was compared.</p>
      ) : (
        <ul className="mt-3 space-y-3">
          {detail.matches.map((m) => (
            <li key={m.referenceId ?? m.plan ?? m.name} className="rounded-md border border-border p-3">
              <div className="flex items-center justify-between gap-3">
                <span className="min-w-0 font-medium">
                  {m.name || (m.plan ? `${planName(m.plan)} reference` : 'Reference')}
                  {m.plan && m.name && m.name !== planName(m.plan) ? (
                    <span className="ml-1.5 text-xs font-normal text-muted-foreground">{planName(m.plan)}</span>
                  ) : null}
                </span>
                <span
                  className={cn(
                    'rounded-full border px-2 py-0.5 text-xs font-semibold tabular-nums',
                    m.score >= (detail.threshold ?? 50) ? TONES.good : m.score >= 25 ? TONES.warn : TONES.quiet,
                  )}
                >
                  {m.score} / 100 · {m.verdict}
                </span>
              </div>
              {m.signals?.length ? (
                <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
                  {m.signals.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-xs text-muted-foreground">No signals in common.</p>
              )}
            </li>
          ))}
        </ul>
      )}
      {scan && (
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
          <Fact label="Live channels" value={scan.liveChannels} />
          <Fact label="Live categories" value={scan.liveCategories} />
          <Fact label="Movie categories" value={scan.vodCategories} />
          <Fact label="Series categories" value={scan.seriesCategories} />
          <Fact label="API" value={scan.apiType} />
          <Fact label="Timezone" value={scan.timezone} />
          <Fact label="Server software" value={scan.serverSoftware} />
          <Fact label="EPG" value={scan.epgUrl ? 'Listed' : 'None'} />
          <Fact label="Logo hosts" value={scan.logoHosts} />
        </dl>
      )}
      {detail.threshold ? (
        <p className="mt-3 text-xs text-muted-foreground">Genuine means a score of {detail.threshold} or more against any reference.</p>
      ) : null}
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="truncate font-mono">{value === '' || value === null || value === undefined ? '—' : String(value)}</dd>
    </div>
  );
}
